import { jwtDecode } from 'jwt-decode';

/**
 * Token sesji — jedno miejsce, z którego czyta cały front (decyzja Bartka
 * 02.11.2026, „Zaloguj jako”).
 *
 * Zwykła sesja trzyma token w `localStorage` (wszystkie karty tego konta).
 * Karta „Zaloguj jako” trzyma token przejętego konta w `sessionStorage` —
 * tylko ta karta, więc karta administratora zostaje na jego koncie.
 * **Najpierw `sessionStorage`**: w karcie przejęcia każde żądanie idzie
 * z tokenem przejętego konta.
 *
 * Karta po zakończonym przejęciu dostaje znacznik „zakończone” i **nie wraca
 * po cichu do konta administratora** z `localStorage` — inaczej pasek by
 * zniknął, a karta pracowałaby dalej jako administrator, choć wyglądała
 * jak sesja pracownika. Wylogowanie i 401 w takiej karcie nigdy nie
 * kasują tokenu administratora.
 */

export const LOGIN_TOKEN_KEY = 'token';
export const IMPERSONATION_TOKEN_KEY = 'impersonationToken';
export const IMPERSONATION_ENDED_KEY = 'impersonationEnded';
/**
 * Znacznik dla innych kart tej przeglądarki (`localStorage`): trwa praca jako
 * inny użytkownik. Karta bez sesji przejęcia — także otwarta odnośnikiem
 * z karty przejęcia, która nie dostaje jej `sessionStorage` — pokazuje wtedy
 * ostrzeżenie, że sama działa na koncie z logowania (przegląd #582, D5).
 */
export const IMPERSONATION_MARKER_KEY = 'impersonationActiveTabs';

/**
 * Ekran karty przejęcia: wymiana kodu i „sesja zakończona”. Aplikacja ma
 * trasy we fragmencie adresu (`/#/…`), więc parametry ekranu też są we
 * fragmencie — nie idą do serwera ani w nagłówku `Referer`.
 */
export const IMPERSONATE_PATH = '/impersonate';
/** „Sesja zakończona” — trasa dla react-admina (`redirectTo`). */
export const IMPERSONATE_ENDED_PATH = `${IMPERSONATE_PATH}?ended=1`;
/** „Sesja zakończona” — pełny adres dla `window.location`. */
export const IMPERSONATE_ENDED_URL = `/#${IMPERSONATE_ENDED_PATH}`;
/** Ekran logowania — po utracie zwykłej sesji. */
export const LOGIN_PATH = '/login';

function tabStorage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function sharedStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

const read = (s: Storage | null, key: string): string | null => {
  try {
    return s?.getItem(key) ?? null;
  } catch {
    return null;
  }
};

/** Token zwykłego logowania (administratora w karcie przejęcia też). */
export function loginToken(): string | null {
  return read(sharedStorage(), LOGIN_TOKEN_KEY);
}

/** Czy ta karta pracuje jako inny użytkownik („Zaloguj jako”). */
export function isImpersonationTab(): boolean {
  return read(tabStorage(), IMPERSONATION_TOKEN_KEY) !== null;
}

/** Czy ta karta była kartą przejęcia, które już się skończyło. */
export function impersonationEnded(): boolean {
  return read(tabStorage(), IMPERSONATION_ENDED_KEY) !== null;
}

/** Token, z którym ta karta pyta API. Karta po przejęciu — żaden. */
export function sessionToken(): string | null {
  const impersonation = read(tabStorage(), IMPERSONATION_TOKEN_KEY);
  if (impersonation) return impersonation;
  if (impersonationEnded()) return null;
  return loginToken();
}

/** Karta zaczyna pracę jako inny użytkownik. */
export function startImpersonationTab(token: string): void {
  const s = tabStorage();
  s?.removeItem(IMPERSONATION_ENDED_KEY);
  s?.setItem(IMPERSONATION_TOKEN_KEY, token);
}

/** Koniec przejęcia w tej karcie — token celu znika, karta zostaje „zakończona”. */
export function endImpersonationTab(): void {
  const info = impersonationInfo();
  if (info) clearImpersonationMarker(info.sessionId);
  const s = tabStorage();
  s?.removeItem(IMPERSONATION_TOKEN_KEY);
  s?.setItem(IMPERSONATION_ENDED_KEY, '1');
}

/** Karta po zakończonym przejęciu wraca do konta z logowania (świadomie, przyciskiem). */
export function leaveEndedImpersonation(): void {
  tabStorage()?.removeItem(IMPERSONATION_ENDED_KEY);
}

/** Zwykłe logowanie w tej karcie — zapis tokenu dla wszystkich kart konta. */
export function storeLoginToken(token: string): void {
  const s = tabStorage();
  s?.removeItem(IMPERSONATION_TOKEN_KEY);
  s?.removeItem(IMPERSONATION_ENDED_KEY);
  // Logowanie (także innego konta) — znacznik przejęcia poprzedniej sesji tej
  // przeglądarki nie dotyczy nowego konta (ponowny przegląd #582, U-c).
  forgetImpersonationMarkers();
  sharedStorage()?.setItem(LOGIN_TOKEN_KEY, token);
}

/** Wszystkie znaczniki przejęcia tej przeglądarki — przy wylogowaniu i logowaniu. */
export function forgetImpersonationMarkers(): void {
  try {
    sharedStorage()?.removeItem(IMPERSONATION_MARKER_KEY);
  } catch {
    // Brak magazynu — nie ma czego czyścić.
  }
}

/**
 * Utrata sesji (wylogowanie, 401). Karta przejęcia kończy tylko swoje
 * przejęcie — token administratora w `localStorage` zostaje nietknięty.
 */
export function clearSessionToken(): void {
  if (isImpersonationTab() || impersonationEnded()) {
    endImpersonationTab();
    return;
  }
  // Wylogowanie zwykłej karty kończy pracę w tej przeglądarce — znaczniki
  // przejęć znikają razem z tokenem (ponowny przegląd #582, U-c).
  forgetImpersonationMarkers();
  sharedStorage()?.removeItem(LOGIN_TOKEN_KEY);
}

/** Dokąd po utracie sesji (adres dla `window.location`): karta przejęcia — „sesja zakończona”, reszta — logowanie. */
export function sessionLostPath(): string {
  return isImpersonationTab() || impersonationEnded()
    ? IMPERSONATE_ENDED_URL
    : LOGIN_PATH;
}

/** Kto przejął konto w tej karcie — z tokenu przejęcia (`impersonatedBy`). */
export interface ImpersonationInfo {
  sessionId: string;
  impersonator: { id: number; username: string; role: string; name: string };
  target: { id: number; username: string };
  /** Koniec ważności sesji (ms od epoki); bez przedłużania. */
  expiresAt: number | null;
}

interface ImpersonationPayload {
  sub?: number;
  username?: string;
  imp?: string;
  exp?: number;
  impersonatedBy?: { id?: number; username?: string; role?: string; name?: string };
}

/** Przejęcie w tej karcie albo `null` (zwykła sesja, token nieczytelny). */
export function impersonationInfo(): ImpersonationInfo | null {
  const token = read(tabStorage(), IMPERSONATION_TOKEN_KEY);
  if (!token) return null;
  try {
    const p = jwtDecode<ImpersonationPayload>(token);
    const by = p.impersonatedBy;
    if (typeof p.imp !== 'string' || !by || typeof by.id !== 'number') return null;
    return {
      sessionId: p.imp,
      impersonator: {
        id: by.id,
        username: by.username ?? '',
        role: by.role ?? '',
        name: by.name || by.username || '',
      },
      target: { id: Number(p.sub), username: p.username ?? '' },
      expiresAt: typeof p.exp === 'number' ? p.exp * 1000 : null,
    };
  } catch {
    return null;
  }
}

/** Przejęcie trwające w którejś karcie tej przeglądarki. */
export interface ImpersonationMarker {
  sessionId: string;
  /** Kogo przejęto — imię i nazwisko albo login. */
  target: string;
  /** Koniec ważności sesji (ms od epoki). */
  expiresAt: number;
}

function readMarkers(): ImpersonationMarker[] {
  try {
    const raw = sharedStorage()?.getItem(IMPERSONATION_MARKER_KEY);
    const list = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(list)
      ? list.filter(
          (m): m is ImpersonationMarker =>
            typeof m?.sessionId === 'string' && typeof m?.target === 'string' && typeof m?.expiresAt === 'number',
        )
      : [];
  } catch {
    return [];
  }
}

function writeMarkers(list: ImpersonationMarker[]): void {
  try {
    const s = sharedStorage();
    if (!s) return;
    if (list.length === 0) s.removeItem(IMPERSONATION_MARKER_KEY);
    else s.setItem(IMPERSONATION_MARKER_KEY, JSON.stringify(list));
  } catch {
    // Znacznik to ostrzeżenie dla innych kart — brak magazynu go nie zatrzymuje.
  }
}

/** Karta przejęcia ogłasza innym kartom, że trwa praca jako `target`. */
export function markImpersonationActive(marker: ImpersonationMarker, now: number = Date.now()): void {
  const others = readMarkers().filter((m) => m.sessionId !== marker.sessionId && m.expiresAt > now);
  writeMarkers([...others, marker]);
}

/** Koniec przejęcia — znacznik znika. */
export function clearImpersonationMarker(sessionId: string): void {
  writeMarkers(readMarkers().filter((m) => m.sessionId !== sessionId));
}

/**
 * Przejęcia trwające w innych kartach — dla karty, która sama nie pracuje
 * jako inny użytkownik. Po czasie sesji znacznik nic nie znaczy.
 */
export function impersonationsInOtherTabs(now: number = Date.now()): ImpersonationMarker[] {
  if (isImpersonationTab()) return [];
  return readMarkers().filter((m) => m.expiresAt > now);
}
